import { NetworkStatus, useQuery, useReactiveVar } from '@apollo/client';
import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetQuestsQuery, GetQuestsQueryVariables, QuestStatus } from '../client/generated/graphql';
import { getQuestsQuery } from '../client/queries/quest';
import { ListQuest } from '../client/types';
import { activeQuestCompletions } from '../client/apollo';
import { QuestCalendar } from '../components/quest/QuestCalendar';
import { QuestCalendarFullView } from '../components/quest/QuestCalendarFullView';
import { QuestDay } from '../components/quest/QuestDay';
import { QuestHeader } from '../components/quest/QuestHeader';
import { QuestHeaderToday } from '../components/quest/QuestHeaderToday';
import { QuestListSkeleton } from '../components/quest/skeleton/QuestListSkeleton';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';
import { getToday, getWeekRange } from '../helpers/datetime';
import { useSelectedDateRestore } from '../hooks/useSelectedDateRestore';
import { questViewPath, ritualViewPath } from '../navigation/paths';

export const QuestListPage: FC = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useSelectedDateRestore();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const goalsEnabled = useFeatureFlag('goals');
  const [loadedUntil, setLoadedUntil] = useState(() => getToday().add(7, 'days').hour(23).minute(59).second(59).millisecond(999));
  
  const initialRange = useMemo(() => getWeekRange(getToday()), []);
  const activeCompletions = useReactiveVar(activeQuestCompletions);
  
  const { data, fetchMore, networkStatus } = useQuery<GetQuestsQuery, GetQuestsQueryVariables>(getQuestsQuery, {
    variables: {
      dueDateFrom: undefined,
      dueDateTo: initialRange.end as string | undefined
    },
    notifyOnNetworkStatusChange: true
  });

  const filteredQuests = useMemo(() => data?.quests?.filter(q => q.status !== QuestStatus.Done) ?? [], [data]);
  const filteredQuestsAndCompletions = useMemo(() => {
    const completionQuestIds = new Set(activeCompletions.keys());
    const completionQuests = Array.from(activeCompletions.values()).map(completion => {
      const matchingQuest = completion.eventLog
        ? filteredQuests.find(quest => quest.id === completion.quest.id)
        : undefined;
      const completionDueDate = matchingQuest?.dueDate ?? completion.quest.dueDate;
      return {
        ...completion.quest,
        dueDate: completionDueDate
          ? dayjs()
            .hour(dayjs(completionDueDate).hour())
            .minute(dayjs(completionDueDate).minute())
            .second(dayjs(completionDueDate).second())
            .millisecond(dayjs(completionDueDate).millisecond())
            .toISOString()
          : new Date().toISOString(),
      } as ListQuest;
    });
    return [...filteredQuests.filter(quest => !completionQuestIds.has(quest.id)), ...completionQuests];
  }, [activeCompletions, filteredQuests]);
  const categoryMap = useMemo(() => Object.fromEntries(data?.categories.map(c => [c.id, c]) ?? []), [data]);
  const goalMap = useMemo(() => Object.fromEntries(data?.goals.map(g => [g.id, g]) ?? []), [data]);

  useEffect(() => {
    if (selectedDate.isAfter(loadedUntil)) {
      const newLoadedUntil = selectedDate.add(7, 'days').hour(23).minute(59).second(59).millisecond(999);
      setLoadedUntil(newLoadedUntil);
      
      const fetchFrom = loadedUntil.add(1, 'day').hour(0).minute(0).second(0).millisecond(0);
      
      fetchMore({
        variables: {
          dueDateFrom: fetchFrom.toISOString() as string | undefined,
          dueDateTo: newLoadedUntil.toISOString() as string | undefined
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult;
          
          const existingQuestIds = new Set(previousResult.quests.map(q => q.id));
          const newQuests = fetchMoreResult.quests.filter(q => !existingQuestIds.has(q.id));
          
          return {
            ...previousResult,
            quests: [...previousResult.quests, ...newQuests]
          };
        }
      });
    }
  }, [selectedDate, loadedUntil, fetchMore]);
  
  const isRefetching = networkStatus === NetworkStatus.fetchMore;
  
  if (networkStatus === NetworkStatus.loading) return <QuestListSkeleton selectedDate={selectedDate} />;

  const handleOnQuestClick = (quest: ListQuest) => {
    const path = quest.ritualType 
      ? ritualViewPath.replace(':id', quest.id)
      : questViewPath.replace(':id', quest.id);
    navigate(path);
  }
  return (
    <>
      <Stack height="100%" paddingTop="calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))">
        <QuestCalendar
            selectedDate={selectedDate}
            isCalendarOpen={isCalendarOpen}
            onToggleCalendar={() => setIsCalendarOpen(open => !open)}
        />
        {goalsEnabled
          ? <QuestHeader categoryMap={categoryMap} goals={data?.goals} goalCompletion={data?.user.goalCompletion} />
          : <QuestHeaderToday quests={filteredQuestsAndCompletions} categoryMap={categoryMap} />
        }
        <Stack sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <AnimatePresence initial={false}>
            {isCalendarOpen ? (
              <motion.div
                key="calendar"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}
              >
                <QuestCalendarFullView
                  selectedDate={selectedDate}
                  onDateSelected={(d) => {
                    setSelectedDate(d);
                    setIsCalendarOpen(false);
                  }}
                />
              </motion.div>
            ) : (
              <motion.div
                key="quests"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}
              >
                <QuestDay
                  quests={filteredQuestsAndCompletions}
                  categoryMap={categoryMap}
                  goalMap={goalsEnabled ? goalMap : {}}
                  selectedDate={selectedDate}
                  onItemClick={handleOnQuestClick}
                  loading={isRefetching}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Stack>
      </Stack>
    </>
  );
};
